import { useState, useEffect, useRef, useCallback } from "react";
import i18n from "../i18n/i18n";

// Images 
import image1 from '../assets/images/gallery/photo_2025-09-09_10-52-27.jpg';
import image2 from '../assets/images/gallery/photo_2025-09-20_21-06-12.jpg';
import image3 from '../assets/images/gallery/photo_2025-10-20_18-46-49.jpg';
import image4 from '../assets/images/gallery/photo_2025-10-29_20-39-16.jpg';
import image5 from '../assets/images/gallery/photo_2025-11-02_15-12-41.jpg';
import image6 from '../assets/images/gallery/photo_2025-11-14_01-39-03.jpg';
import image7 from '../assets/images/gallery/photo_2025-11-14_01-39-09.jpg';
import image8 from '../assets/images/gallery/photo_2025-11-14_01-41-49.jpg';


interface Picture {
  id: number;
  src: string;
  title: string;
}

interface BirthdayRevealProps {
  onComplete?: () => void;
}

const BirthdayReveal = ({ onComplete }: BirthdayRevealProps) => {
  const galleryPictures: Picture[] = [
    { id: 1, src: image1, title: i18n.t('romantic_1') },
    { id: 2, src: image2, title: i18n.t('romantic_2') },
    { id: 3, src: image3, title: i18n.t('romantic_3') },
    { id: 4, src: image4, title: i18n.t('romantic_4') },
    { id: 5, src: image5, title: i18n.t('romantic_5') },
    { id: 6, src: image6, title: i18n.t('romantic_6') },
    { id: 7, src: image7, title: i18n.t('romantic_7') },
    { id: 8, src: image8, title: i18n.t('romantic_8') },
  ];

  const [pictures, setPictures] = useState<Picture[]>([]);
  const [rotationY, setRotationY] = useState(0);
  const [showCarousel, setShowCarousel] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // State for drag interactions
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentRotation = useRef(0);
  const autoSpinEnabled = useRef(true);

  useEffect(() => {
    setPictures(galleryPictures);
    currentRotation.current = 0;
  }, [galleryPictures]);

  // Auto advance after 30 seconds of carousel
  useEffect(() => {
    if (showCarousel && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 30000); // 30 seconds
      return () => clearTimeout(timer);
    }
  }, [showCarousel, onComplete]);
  
  // --- Auto-Spin Logic ---
  useEffect(() => {
    let interval: number | undefined;

    const startSpin = () => {
        if (!interval) {
            interval = window.setInterval(() => {
                if (autoSpinEnabled.current) {
                    setRotationY(prev => prev + 0.2);
                }
            }, 30); // The speed
        }
    };

    const stopSpin = () => {
        clearInterval(interval);
        interval = undefined;
    };

    startSpin();

    return () => {
        stopSpin();
    };
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    autoSpinEnabled.current = false;
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    startX.current = clientX;
    carouselRef.current?.classList.add('cursor-grabbing');
  }, []);

  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
    carouselRef.current?.classList.remove('cursor-grabbing');
    currentRotation.current = rotationY; 
    
    setTimeout(() => {
        autoSpinEnabled.current = true;
    }, 500); 

  }, [rotationY]);

  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging.current) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const deltaX = clientX - startX.current;
    const sensitivity = 0.2; 
    
    const newRotation = currentRotation.current + deltaX * sensitivity;
    setRotationY(newRotation);
  }, []);
  
  // Set up event listeners for dragging outside of the carousel element
  useEffect(() => {
    const handleMouseUp = () => isDragging.current && handleDragEnd();
    const handleMouseMove = (e: MouseEvent) => isDragging.current && handleDragMove(e);
    const handleTouchMove = (e: TouchEvent) => isDragging.current && handleDragMove(e);

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleDragEnd, handleDragMove]);

  const numPictures = pictures.length;
  const angle = numPictures > 0 ? 360 / numPictures : 0;
  const cylinderRadiusZ = 450; 
  const pitchRotationX = -8; 
  
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 overflow-hidden">
      
      <div 
        className="relative w-full"
        style={{ 
          perspective: '2000px',
          perspectiveOrigin: '50% 50%',
          height: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        
        <div 
          ref={carouselRef}
          className="cursor-grab"
          style={{ 
            transformStyle: 'preserve-3d',
            transform: `rotateX(${pitchRotationX}deg) rotateY(${rotationY}deg)`,
            transition: 'transform 0.1s ease-out',
            position: 'relative',
            width: '100%',
            height: '100%'
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          {pictures.map((pic, index) => {
            const itemRotationY = index * angle;
            
            return (
              <div
                key={pic.id}
                className="absolute w-56 h-80 md:w-64 md:h-96 rounded-xl shadow-2xl transition-all duration-500 ease-out group hover:shadow-3xl overflow-hidden"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: `rotateY(${itemRotationY}deg) translateZ(${cylinderRadiusZ}px)`,
                  transitionDelay: `${index * 50}ms`,
                  left: '50%',
                  top: '50%',
                  marginLeft: '-112px',
                  marginTop: '-160px',
                }}
              >
                <div 
                  className="w-full h-full overflow-hidden rounded-xl relative"
                  style={{
                    backgroundColor: 'black', 
                  }}
                >
                  <img
                    src={pic.src}
                    alt={pic.title}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-105"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4 rounded-xl">
                    <p className="text-white font-semibold text-base transition-opacity duration-300">
                      {pic.title}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BirthdayReveal;